/**
 * Registry for the /product/:slug content pages. Each entry is one crawlable,
 * SEO-first feature/trust page. Adding a page is a data change here: no new
 * route, no controller change. Rendered by the app's product page and the crawler snapshot.
 *
 * Block shapes consumed by ProductBlocks:
 *   { type: "prose",    heading?, body }
 *   { type: "features", heading?, items: [{ icon, title, body }] }
 *   { type: "steps",    heading?, items: [{ title, body }] }
 *   { type: "stats",    heading?, items: [{ value, label }] }
 *   { type: "callout",  title, body, cta?: { label, to } }
 *
 * Copy rules: no em-dashes, no emoji (Stanza customer-copy convention).
 */
import { PRODUCT_PAGE_SLUGS } from "./sitemap.js";
export const PRODUCT_PAGES = [
    {
        slug: PRODUCT_PAGE_SLUGS.GROUPS,
        blurb: "Every team and show in one calendar.",
        eyebrow: "Stanza+",
        seoTitle: "Stanza Groups: bundle teams and shows into one calendar",
        seoDescription: "Groups bundle the teams, leagues, and shows you follow into a single calendar, kept complete and up to date. A Stanza+ feature.",
        hero: {
            title: "Bundle what you follow",
            subtitle: "A Group folds the teams, leagues, and shows you follow into a single calendar, so you add them all at once and it stays complete as new events land.",
        },
        heroVisual: {
            frames: [
                {
                    badge: "Stanza+ · Group",
                    title: "Netflix",
                    rows: [
                        { label: "Stranger Things", meta: "New episode" },
                        { label: "Wednesday", meta: "Season 2" },
                        { label: "The Witcher", meta: "New episode" },
                    ],
                },
                {
                    badge: "Stanza+ · Group",
                    title: "NFL",
                    rows: [
                        { label: "Chiefs", meta: "Sun 1:00 PM" },
                        { label: "Eagles", meta: "Sun 4:25 PM" },
                        { label: "49ers", meta: "Mon 8:15 PM" },
                    ],
                },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "One calendar for a whole side of your life",
                tldr: "Many calendars, one subscription",
                body: "Nobody follows just one thing. You follow a league, a couple of teams in it, and the three shows you are actually watching. Added one by one, that is a stack of separate calendars cluttering your sidebar, each with its own color and its own toggle. A Group folds them into a single calendar: one name, one color, one subscription on your phone. Your calendar app treats it as one thing. Stanza does the work of keeping everything inside it complete.",
            },
            {
                type: "features",
                heading: "What a Group gives you",
                items: [
                    { icon: "groups", title: "One subscription, many calendars", body: "Add a whole league, a conference, or your personal watchlist in a single step instead of ten." },
                    { icon: "sync", title: "Stays complete on its own", body: "When a game moves or a new season is announced, every calendar inside the Group updates together. You never re-add anything." },
                    { icon: "tune", title: "Yours to shape", body: "Start from a ready-made Group like a full league, then trim it to just the teams you care about. It keeps updating either way." },
                    { icon: "calendar_view_month", title: "One tidy sidebar", body: "One name and one color in your calendar app instead of a dozen separate entries." },
                    { icon: "ios_share", title: "Share the bundle", body: "Share a Group and someone adds every calendar inside it in one step." },
                ],
            },
            {
                type: "steps",
                heading: "How to add a Group",
                items: [
                    { title: "Find a calendar to add", body: "Browse to any team, league, or show you want to follow." },
                    { title: "Add to Group", body: "Create a new Group or add it to one you already have." },
                    { title: "Trim to taste", body: "Head to your library to add or remove calendars from your Group." },
                ],
            },
            {
                type: "prose",
                heading: "Two Groups people actually make",
                body: "The classic is a league Group: every NFL or Premier League team in one calendar for the season, so any game you might want to catch is already on your phone. The other is a watchlist: the shows you are currently watching in one place, so premieres and new episodes land together instead of scattered across a dozen subscriptions. Plenty of members keep both.",
            },
            {
                type: "logos",
                rails: [
                    { set: "leagues", heading: "Group your teams" },
                    { set: "streaming", heading: "Group your shows" },
                ],
            },
        ],
        faqs: [
            { q: "What is a Stanza Group?", a: "A Group is a calendar that bundles the teams, leagues, and shows you follow, so all of them live in one place instead of a stack of separate subscriptions. Groups are part of Stanza+." },
            { q: "Can I mix sports and TV in one Group?", a: "Yes. A Group can hold anything on Stanza in any combination. A common setup is one Group for your teams and another for the shows you are currently watching, but a single mixed Group works fine too." },
            { q: "What happens on my phone when I add a Group?", a: "Your calendar app sees one calendar with one name and one color. Every game and episode from the calendars inside it appears under that single calendar, so your sidebar stays short." },
            { q: "Can I add or remove calendars from a Group later?", a: "Yes, anytime, from your library. Removing one calendar only drops that calendar's events. Everything else stays and keeps updating." },
            { q: "Do Groups update automatically?", a: "Yes. When a game moves or a new season is announced for anything inside the Group, the change flows into the same single calendar with nothing to re-add." },
            { q: "Do I need Stanza+ to create a Group?", a: "Yes, Groups are a Stanza+ feature, along with unlimited calendars and faster updates." },
        ],
        related: [
            { label: "Netflix", to: "/tag/netflix" },
            { label: "NFL", to: "/tag/nfl" },
            { label: "How sports fans use Stanza", to: "/product/how-sports-fans-use-stanza" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.HOW_STANZA_GETS_SCHEDULES,
        blurb: "Official feeds and creators, always sourced.",
        eyebrow: "How it works",
        seoTitle: "How Stanza gets its schedules: data feeds and creators",
        seoDescription: "Every Stanza calendar comes from a trusted source: official data feeds for major sports and TV, plus creators who manage their own schedules on Stanza.",
        hero: {
            title: "Where schedules come from",
            subtitle: "Every calendar has a clear source. Major sports and TV flow in from official data feeds, and creators publish and maintain the rest right here.",
            cta: { label: "Add 'Em All", to: "/upgrade/plus" },
        },
        heroVisual: {
            frames: [
                {
                    badge: "Source · Data feed",
                    title: "Premier League",
                    rows: [
                        { label: "Arsenal vs Chelsea", meta: "Sat 12:30 PM" },
                        { label: "Liverpool vs City", meta: "Sun 4:30 PM" },
                    ],
                },
                {
                    badge: "Source · Creator",
                    title: "City Runners Club",
                    rows: [
                        { label: "Track night", meta: "Tue 6:00 PM" },
                        { label: "Long run", meta: "Sun 8:00 AM" },
                    ],
                },
            ],
        },
        blocks: [
            {
                type: "features",
                heading: "Two trusted sources",
                items: [
                    {
                        icon: "database",
                        title: "Official data feeds",
                        body: "For major sports and TV, we pull from official and licensed providers, then convert every start time to your device. Fixtures for the NFL, NBA, and Premier League flow in automatically.",
                        examples: [
                            { name: "NFL", tag: "nfl" },
                            { name: "NBA", tag: "nba" },
                            { name: "Premier League", tag: "premier-league" },
                        ],
                    },
                    {
                        icon: "verified",
                        title: "Creators who manage their own",
                        body: "Leagues, clubs, colleges, and independent organizers publish and maintain their schedules on Stanza. A local running club or a college program keeps its own calendar current.",
                        examples: [
                            { name: "Kid Hitman Snatcher", handle: "kid-hitman-snatcher" },
                            { name: "FC Inter", handle: "inter" },
                        ],
                    },
                ],
            },
            {
                type: "timeline",
                heading: "What happens overnight",
                items: [
                    { tag: "EVERY NIGHT", title: "We re-pull every automated schedule", body: "60,000 plus teams, leagues, and shows, compared against what we published the day before." },
                    { tag: "CHANGES LAND", title: "Moves, postponements, new fixtures", body: "Moved games get their new start times, and TV episodes pick up air dates as networks confirm them." },
                    { tag: "BY MORNING", title: "Your feed reflects everything", body: "The calendar your app reads matches everything the source changed. Creator edits go live for subscribers immediately." },
                ],
            },
            {
                type: "prose",
                heading: "Schedules are messier than they look",
                tldr: "Your calendar corrects itself",
                body: "Real schedules are moving targets. The NFL flexes games to primetime with a few weeks notice. Playoff times start as TBD and firm up round by round. Streaming services announce a premiere long before an episode list exists. Our approach: publish what the source has confirmed, then let the calendar correct itself as details firm up. The event on your phone quietly moves to the right time; you never have to go hunting for the change.",
            },
            {
                type: "prose",
                heading: "And when something breaks",
                body: "Feeds go down, links die, and a source occasionally publishes something wrong. We monitor for calendars that stop updating and for links that stop resolving, and fix them rather than leaving them up to mislead you. If you spot an error we missed, every calendar has a way to flag it, and reports go to a person who can actually fix the schedule.",
            },
            {
                type: "callout",
                title: "Manage a schedule?",
                body: "Publish your league, team, or event on Stanza for free and keep fans in sync.",
                cta: { label: "Stanza for creators", to: "/creators" },
            },
        ],
        faqs: [
            { q: "Where do Stanza calendars come from?", a: "Two places: official and licensed data feeds for major sports and TV, and creators who publish and maintain their own schedules directly on Stanza." },
            { q: "How often do automated schedules refresh?", a: "Overnight, every night. Any change at the source, from a moved kickoff to a newly announced fixture, is reflected by morning. Stanza+ calendars pick up changes even faster." },
            { q: "What happens when a game time changes?", a: "The change flows into the feed your calendar app subscribes to, and the event on your phone updates itself on the next sync. Reminders you set on the event move with it." },
            { q: "How do you handle TBD and flexed games?", a: "We publish what the league has confirmed. A playoff game listed as TBD gets its real time the moment it is announced, and a flexed NFL game moves to its new slot automatically." },
            { q: "Are creator-managed calendars reliable?", a: "They are maintained by the organizers themselves, and every calendar shows when it last updated, so you can see it is being kept current before you rely on it." },
            { q: "Can I publish my own schedule?", a: "Yes. Creators can publish a calendar on Stanza for free, and every subscriber stays in sync automatically as they make changes." },
        ],
        related: [
            { label: "For creators", to: "/creators" },
            { label: "Transparent Updates", to: "/product/transparent-updates" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.HOW_CALENDAR_APPS_UPDATE,
        blurb: "Why subscriptions stay current on their own.",
        eyebrow: "Technical",
        seoTitle: "How subscription calendars stay up to date (technical guide)",
        seoDescription: "A technical look at how calendar subscriptions work: the iCalendar standard, webcal feeds, and why Apple, Google, and Outlook refresh on their own.",
        hero: {
            title: "How calendars stay current",
            subtitle: "A subscription calendar is a live feed, not a one-time import. Here is what actually happens between Stanza and your calendar app.",
        },
        heroVisual: {
            frames: [
                {
                    badge: "Subscribed",
                    title: "Lakers at Warriors",
                    rows: [
                        { label: "Tip-off", meta: "Wed 7:30 PM" },
                        { label: "Venue", meta: "Chase Center" },
                    ],
                },
                {
                    badge: "Updated just now",
                    title: "Lakers at Warriors",
                    rows: [
                        { label: "Tip-off", meta: "Wed 10:00 PM" },
                        { label: "Venue", meta: "Chase Center" },
                    ],
                },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "Subscriptions, not imports",
                tldr: "A live feed, not a snapshot",
                body: "A subscription calendar is a live feed, not a one-time snapshot. When you add a Stanza calendar you subscribe to a feed served over webcal and https as an iCalendar (.ics) file. Your calendar app stores the feed URL and re-fetches it on a schedule, so later changes reach you without any action.",
            },
            {
                type: "steps",
                heading: "What happens under the hood",
                items: [
                    { title: "You subscribe to a feed URL", body: "Your app saves the webcal link and treats it as a live, read-only calendar." },
                    { title: "Your app polls the feed", body: "Apple, Google, and Outlook each re-fetch the .ics on their own cadence, from roughly hourly to once a day." },
                    { title: "Changes propagate", body: "New events, and any time or venue changes, appear the next time your app refreshes. Removed events drop off." },
                ],
            },
            {
                type: "prose",
                heading: "Why refresh timing varies",
                body: "How often a calendar actually updates is decided by your app, not by us. Stanza always serves a fresh, correct feed with caching headers that invite frequent refreshes, but Apple, Google, and Outlook set their own polling intervals, which is why the same subscription can refresh at different times on different devices.",
            },
            {
                type: "callout",
                title: "Add a calendar that stays current",
                body: "Subscribe once and let it keep itself up to date on every device.",
                cta: { label: "Browse calendars", to: "/" },
            },
        ],
        faqs: [
            { q: "How does a calendar subscription update itself?", a: "Your app stores the feed URL and re-fetches the iCalendar file on a schedule. Any changes in the feed appear on the next refresh, with no re-adding." },
            { q: "How often do calendar apps refresh?", a: "It depends on the app. Apple, Google, and Outlook each poll subscribed calendars on their own cadence, commonly from about hourly to once a day." },
            { q: "Why is my calendar not showing the latest change yet?", a: "The feed is already updated on our side. Your app controls how often it refreshes, so the change appears at its next poll. Removing and re-adding the subscription forces an immediate refresh." },
            { q: "Can I make my calendar app check for updates more often?", a: "Most calendar apps let you trigger a manual refresh, and Stanza+ adds refresh on demand so you never have to wait for the next poll." },
            { q: "Does an updated calendar use a lot of data or battery?", a: "No. Calendar syncs are small text updates, not large downloads, so keeping several subscriptions current has a negligible effect on either." },
        ],
        related: [
            { label: "Transparent Updates", to: "/product/transparent-updates" },
            { label: "How Stanza gets schedules", to: "/product/how-stanza-gets-schedules" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.TRANSPARENT_UPDATES,
        blurb: "See when each calendar last updated.",
        eyebrow: "Trust",
        seoTitle: "Transparent Updates: see when your calendar last refreshed",
        seoDescription: "Transparent Updates shows the last time each Stanza calendar was checked and refreshed, so you can trust that what is on your schedule is current.",
        hero: {
            title: "Always know it's current",
            subtitle: "Transparent Updates shows the last time each calendar was verified and refreshed, so you never have to wonder whether a schedule is current.",
        },
        heroVisual: {
            frames: [
                {
                    badge: "Last checked · 2 min ago",
                    title: "NFL Schedule",
                    rows: [
                        { label: "Chiefs vs Eagles", meta: "Sun 4:25 PM" },
                        { label: "Status", meta: "Up to date" },
                    ],
                },
                {
                    badge: "Last checked · Just now",
                    title: "NFL Schedule",
                    rows: [
                        { label: "Chiefs vs Eagles", meta: "Sun 8:20 PM" },
                        { label: "Status", meta: "Updated" },
                    ],
                },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "The problem with every schedule site",
                body: "Anyone can put a schedule on the internet. The question a fan actually has is different: is this still right? A page that was accurate in August can be quietly wrong by October, and you only find out when you show up an hour late. Most sites ask you to take freshness on faith. We decided to just show it.",
            },
            {
                type: "features",
                heading: "What you see on every calendar",
                items: [
                    { icon: "schedule", title: "A clear last-checked time", body: "Every calendar shows when it was last verified against its source, so a quiet week reads as up to date rather than abandoned." },
                    { icon: "fact_check", title: "Proof it is maintained", body: "You can see the schedule is actively kept current, not a snapshot someone added once and forgot." },
                    { icon: "notifications_active", title: "We watch for breaks", body: "Calendars that stop refreshing and links that stop resolving are flagged to our team and fixed instead of silently going stale." },
                    { icon: "flag", title: "Flag anything wrong", body: "Every calendar has a report path, and reports go to a person who can fix the schedule." },
                    { icon: "update", title: "Refresh on demand", body: "Stanza+ members can pull the latest immediately instead of waiting for the next check." },
                ],
            },
            {
                type: "prose",
                heading: "Quiet is not the same as stale",
                tldr: "The last-checked time tells them apart",
                body: "An off-season calendar with no events for two months is doing its job. A calendar whose source died two months ago is not, even if it looks identical at a glance. The last-checked time is what separates them, and it is why we put the timestamp where you can see it instead of burying it. Before a big game or premiere, one glance answers the only question that matters: can I rely on this.",
            },
            {
                type: "callout",
                title: "Follow calendars you can trust",
                body: "Every Stanza calendar shows when it was last checked, so you always know it is current.",
                cta: { label: "Browse calendars", to: "/" },
            },
        ],
        faqs: [
            { q: "How do I know when a Stanza calendar last updated?", a: "Each calendar shows a last-verified time, so before a big game or premiere you can confirm at a glance that the times you see were checked recently." },
            { q: "What if a calendar stops updating?", a: "We monitor for feeds that go quiet and links that die, and they get flagged and fixed rather than left up to mislead you." },
            { q: "Does a calendar with no recent events mean it is broken?", a: "No. Off-seasons and mid-season breaks are normal. The last-checked time is what tells you a quiet calendar is still maintained." },
            { q: "Does a recent last-updated time mean the schedule changed?", a: "Not necessarily. It means the calendar checked its source and is showing the most current schedule available, whether or not anything moved." },
            { q: "What should I do if a calendar looks wrong?", a: "Flag it from the calendar page. Reports go to a person who can fix the schedule, and Stanza+ members can also force a refresh on demand to pull the latest data immediately." },
        ],
        related: [
            { label: "How calendar apps update", to: "/product/how-calendar-apps-update" },
            { label: "How Stanza gets schedules", to: "/product/how-stanza-gets-schedules" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.HOW_SPORTS_FANS_USE_STANZA,
        blurb: "Never miss a kickoff or a release.",
        eyebrow: "For fans",
        seoTitle: "How sports fans use Stanza to never miss a game",
        seoDescription: "See how sports fans use Stanza: follow your teams and leagues, get every game on your phone, and let it all stay up to date automatically.",
        hero: {
            title: "Built for sports fans",
            subtitle: "Follow the teams and leagues you care about, get every game on the calendar you already use, and let Stanza keep it current for you.",
            cta: { label: "Add All Your Teams", to: "/upgrade/plus" },
        },
        heroVisual: {
            frames: [
                {
                    badge: "On your calendar",
                    title: "Gameday",
                    rows: [
                        { label: "Chiefs vs Eagles", meta: "Sun 4:25 PM" },
                        { label: "Reminder", meta: "1 hour before" },
                    ],
                },
                {
                    badge: "On your calendar",
                    title: "Gameday",
                    rows: [
                        { label: "Warriors vs Lakers", meta: "Wed 7:30 PM" },
                        { label: "Reminder", meta: "1 hour before" },
                    ],
                },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "The gameday routine, retired",
                tldr: "One subscription replaces the routine",
                body: "Search the matchup, squint at a time listed in someone else's time zone, do the math, type it in, and hope the league does not move it. Stanza replaces all of that with one subscription. Your team's full season lands on the calendar you already check, every start time converted to wherever you are, and when the league flexes a game to primetime the event on your phone just moves.",
            },
            {
                type: "features",
                heading: "Built for following along",
                items: [
                    { icon: "sports_basketball", title: "Follow teams and leagues", body: "Add a single team in one tap, or a whole league or conference as a Group." },
                    { icon: "event_available", title: "Every game on your phone", body: "Games land on the Apple, Google, or Outlook calendar you already check, in your own time zone." },
                    { icon: "sync", title: "Always up to date", body: "Flexed kickoffs, postponements, and playoff times that firm up round by round all sync on their own." },
                    { icon: "notifications", title: "Reminders that move", body: "Set one reminder and it fires before every game, following any reschedule." },
                    { icon: "download", title: "Just one game", body: "Add a single marquee game without the whole season, a Stanza+ feature." },
                ],
            },
            {
                type: "prose",
                heading: "Set a reminder once, keep it forever",
                body: "Games are real events on your calendar, so everything your calendar app can do works on them. Set a one-hour reminder and it fires before every game; if a game moves, the reminder moves with it. An 8 PM kickoff in London shows as your local afternoon, and games from every league you follow interleave on the same day view, next to the rest of your life, which is exactly where a schedule conflict should show up.",
            },
            {
                type: "callout",
                title: "Start with your league",
                body: "Pick a league or team and add the full schedule in seconds.",
                cta: { label: "Browse sports", to: "/" },
            },
        ],
        faqs: [
            { q: "How do I add my team's schedule to my calendar?", a: "Find your team on Stanza, tap Add to Calendar, and choose Apple, Google, or Outlook. Every game syncs and stays up to date automatically." },
            { q: "Can I follow a whole league at once?", a: "Yes. Add a league Group to get every team in one subscription, then trim it to just the teams you follow. It keeps updating either way." },
            { q: "Will game times show in my time zone?", a: "Yes. Every start time converts to your device's local time, including for international leagues, so a match abroad shows at the correct local hour." },
            { q: "What about flexed and postponed games?", a: "When a game is moved, the event on your phone updates itself, and any reminder you set moves with it. Playoff games listed as TBD pick up their real times as they are announced." },
            { q: "Do I need to check the app for new fixtures?", a: "No. Newly announced games appear on your calendar as they are scheduled. Your calendar app becomes the only place you need to look." },
            { q: "Can I add just one game instead of a season?", a: "Yes. Stanza+ lets you add a single event, so you can grab the rivalry game without carrying all 82." },
        ],
        related: [
            { label: "NFL", to: "/tag/nfl" },
            { label: "NBA", to: "/tag/nba" },
            { label: "Groups", to: "/product/groups" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.QUALITY_CALENDARS,
        blurb: "Sourced, maintained, provably current.",
        eyebrow: "Trust",
        seoTitle: "Quality over quantity: calendars you can actually trust",
        seoDescription: "Stanza favors accurate, maintained calendars over a giant stale list. Every calendar has a source and a visible last-updated time.",
        hero: {
            title: "Quality beats quantity",
            subtitle: "A schedule is only useful if it is right. Stanza favors calendars that are sourced, maintained, and provably current over a longer list that goes stale.",
        },
        heroVisual: {
            frames: [
                {
                    badge: "Verified",
                    title: "Premier League",
                    rows: [
                        { label: "Source", meta: "Official feed" },
                        { label: "Last updated", meta: "Today" },
                        { label: "Status", meta: "Maintained" },
                    ],
                },
                {
                    badge: "Verified",
                    title: "The Bear",
                    rows: [
                        { label: "Source", meta: "Creator" },
                        { label: "Last updated", meta: "Today" },
                        { label: "Status", meta: "Maintained" },
                    ],
                },
            ],
        },
        blocks: [
            {
                type: "features",
                heading: "What quality means here",
                items: [
                    { icon: "verified", title: "Every calendar has a source", body: "Official data feeds or the creators who maintain their own schedules. No mystery calendars." },
                    { icon: "schedule", title: "Provably current", body: "A visible last-updated time on every calendar, so you can see it is maintained." },
                    { icon: "auto_fix_high", title: "Broken links get fixed", body: "We monitor for dead links and stalled feeds, then repair or remove them instead of leaving them up." },
                    { icon: "flag", title: "Flag anything wrong", body: "Reports go to a person who can actually fix the schedule." },
                    { icon: "travel_explore", title: "Coverage grows carefully", body: "New calendars come from official feeds and creators, so growth never outruns accuracy." },
                ],
            },
            {
                type: "prose",
                heading: "Why this is the whole game",
                tldr: "Accuracy is the product",
                body: "Anyone can list thousands of calendars. Keeping them right is the hard part. It is the only part a fan actually feels, and it is where we spend our effort.",
            },
            {
                type: "callout",
                title: "Find a calendar worth trusting",
                body: "Browse sourced, maintained calendars for the teams and shows you follow.",
                cta: { label: "Browse calendars", to: "/" },
            },
        ],
        faqs: [
            { q: "Does Stanza have every team and show?", a: "We focus on calendars we can keep accurate rather than listing everything. Coverage grows through official feeds and creators who maintain their own schedules, and if something you want is missing you can request it from the homepage." },
            { q: "How do I know a calendar is maintained?", a: "Each calendar shows a last-updated time, and we monitor sources so broken or stalled calendars get fixed rather than left up." },
            { q: "What if I find an error in a calendar?", a: "Flag it from the calendar page. Reports go to a person who can fix the schedule, and accuracy reports are how the catalog stays dependable for everyone." },
            { q: "Do you remove calendars that are not maintained?", a: "Calendars that go stale or lose their source are reviewed rather than left to mislead you. Every calendar you can subscribe to should be one that is actually being kept current." },
            { q: "How is this different from a generic calendar import?", a: "A one-time import goes stale the moment a time changes. Stanza calendars stay linked to their source and refresh on their own, so what is on your phone keeps matching reality." },
        ],
        related: [
            { label: "Transparent Updates", to: "/product/transparent-updates" },
            { label: "How Stanza gets schedules", to: "/product/how-stanza-gets-schedules" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.GOOGLE_CONNECT,
        blurb: "Straight into Google Calendar, securely.",
        eyebrow: "Google Connect",
        seoTitle: "Google Connect: add Stanza calendars to Google Calendar",
        seoDescription: "Connect your Google account to add Stanza calendars straight to Google Calendar. A secure OAuth connection you approve and can revoke at any time.",
        hero: {
            title: "Straight to Google Calendar. Across All Devices.",
            subtitle: "",
            cta: { label: "Start 7 Day Free Trial", to: "/upgrade/plus" },
        },
        heroVisual: {
            frames: [
                {
                    badge: "Step 1",
                    title: "Sign in with Google",
                    rows: [
                        { label: "Account", meta: "you@gmail.com" },
                        { label: "Password", meta: "Never shared" },
                    ],
                },
                {
                    badge: "Step 2",
                    title: "Approve access",
                    rows: [
                        { label: "Calendars", meta: "Allowed" },
                        { label: "Email and files", meta: "Not requested" },
                    ],
                },
                {
                    badge: "Step 3",
                    title: "Connected",
                    rows: [
                        { label: "Google Calendar", meta: "Syncing" },
                        { label: "Access", meta: "Revoke anytime" },
                    ],
                },
            ],
        },
        blocks: [
            {
                type: "steps",
                heading: "How the connection works",
                items: [
                    { title: "Sign in with Google", body: "You sign in through Google itself. Stanza never sees or stores your password." },
                    { title: "Approve calendar access", body: "Google shows you exactly what you are granting. You approve, and Stanza receives a limited, revocable token." },
                    { title: "Calendars sync directly", body: "The calendars you add are written straight into Google Calendar and appear on every device signed into your Google account." },
                ],
            },
            {
                type: "prose",
                heading: "Your data, your permissions",
                tldr: "Your password is never shared",
                body: "The connection is a scoped OAuth token issued by Google, not your password, and it only covers the calendars you add, never your email or other Google data. End it whenever you want: toggle Google Connect off in the side menu, or revoke access from your Google Account permissions. Either way it stops immediately.",
            },
        ],
        faqs: [
            { q: "What is Google Connect?", a: "Google Connect lets you add Stanza calendars straight into Google Calendar by securely connecting your Google account, so events sync directly instead of through a separate subscription link." },
            { q: "Is it safe to connect my Google account?", a: "Yes. You sign in through Google, not Stanza, so your password is never shared. Stanza receives a limited token scoped to calendar access, which you can revoke at any time." },
            { q: "What can Stanza see or change?", a: "Only the calendars you add. Stanza uses the connection to keep those calendars up to date, and does not read your email or other Google data." },
            { q: "How do I disconnect Google Connect?", a: "Open the side menu in Stanza and toggle Google Connect off. Your calendars switch back to standard link subscriptions. You can also remove access from your Google Account permissions; either way the connection ends right away." },
            { q: "Do I need Google Connect to use Stanza?", a: "No. You can subscribe to any calendar with a standard link on Apple, Google, or Outlook. Google Connect just makes adding to Google Calendar a single approved step." },
            { q: "Will my Stanza calendars appear on all my Google devices?", a: "Yes. Once a calendar is in Google Calendar, it shows up everywhere you use your Google account and stays in sync as the schedule updates." },
        ],
        related: [
            { label: "How calendar apps update", to: "/product/how-calendar-apps-update" },
            { label: "How Stanza gets schedules", to: "/product/how-stanza-gets-schedules" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.UNLIMITED_CALENDARS,
        blurb: "Follow everything, no cap.",
        eyebrow: "Stanza+",
        seoTitle: "Unlimited calendars: add as many as you want with Stanza+",
        seoDescription: "Free covers two calendars. Stanza+ removes the cap, so every team, show, and league you follow lives in one place, with no limits.",
        hero: {
            title: "Add unlimited calendars",
            subtitle: "Free covers two calendars. Stanza+ removes the cap, so every team, show, and league you follow can live in one place.",
        },
        heroVisual: {
            frames: [
                { badge: "Free", title: "Your calendars", rows: [{ label: "2 of 2 used", meta: "Limit reached" }] },
                { badge: "Stanza+", title: "Your calendars", rows: [{ label: "Lakers", meta: "Added" }, { label: "The Bear", meta: "Added" }, { label: "Premier League", meta: "Added" }] },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "Two calendars is a taste, not a life",
                tldr: "Free covers 2. Stanza+ has no cap",
                body: "The free plan covers two calendars: enough to see how Stanza works, not enough to actually live on. Most members follow a league, a couple of teams inside it, and a rotation of shows, and the moment you have to choose which two deserve a slot, your calendar stops reflecting what you actually follow. Stanza+ removes the cap entirely.",
            },
            {
                type: "features",
                heading: "No cap, one place",
                items: [
                    { icon: "all_inclusive", title: "No limit", body: "Subscribe to as many calendars as you like across sports, TV, movies, and more." },
                    { icon: "inventory_2", title: "Everything together", body: "Every team, show, and league lands on the Apple, Google, or Outlook calendar you already check." },
                    { icon: "sync", title: "All kept current", body: "Each subscription is a lightweight feed that updates on its own, whether you have three or three hundred." },
                    { icon: "groups", title: "Bundle into Groups", body: "Fold a whole league or your watchlist into one calendar so the list stays tidy." },
                    { icon: "explore", title: "The whole library", body: "Sports, TV, movies, and exclusive calendars are all open to add." },
                ],
            },
            {
                type: "callout",
                title: "Follow everything you love",
                body: "Go Stanza+ and add every calendar that matters to you.",
                cta: { label: "See Stanza+", to: "/upgrade/plus" },
            },
        ],
        faqs: [
            { q: "How many calendars can I add on the free plan?", a: "The free plan includes two calendars. You can upgrade to Stanza+ anytime to remove the limit." },
            { q: "Is there really no limit with Stanza+?", a: "There is no cap. Every team in a league, a long list of shows, or both, and each one keeps updating on its own once you add it." },
            { q: "Does adding more calendars slow down my calendar app?", a: "No. Each calendar is a small subscription feed rather than a heavy download, so your app handles many of them without slowing down." },
            { q: "What happens to my calendars if I cancel Stanza+?", a: "Your existing calendars stay on your device. You just cannot add new ones beyond the free limit until you resubscribe." },
            { q: "Will a long list of calendars get cluttered?", a: "Bundle them into Groups. A whole set of teams or shows folds into one calendar, so you can follow a lot without your sidebar turning into a wall of entries." },
        ],
        related: [
            { label: "Group calendars", to: "/product/groups" },
            { label: "Recommendations", to: "/product/recommendations" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.CROSS_DEVICE_SYNC,
        blurb: "One calendar on every device.",
        eyebrow: "Stanza+",
        seoTitle: "Cross-device sync: one calendar on every device with Stanza+",
        seoDescription: "Add a calendar on your phone and see it on your laptop. Stanza+ keeps every device in sync automatically, with no manual export.",
        hero: {
            title: "One calendar, every device",
            subtitle: "Add it on your phone, see it on your laptop. Stanza+ keeps every device in sync automatically, so your calendar is the same everywhere.",
        },
        heroVisual: {
            frames: [
                { badge: "Phone", title: "Added on your phone", rows: [{ label: "Lakers at Warriors", meta: "Wed 7:30 PM" }] },
                { badge: "Laptop", title: "Already on your laptop", rows: [{ label: "Lakers at Warriors", meta: "Synced" }] },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "Subscribe once, everywhere",
                tldr: "Add once, every device follows",
                body: "Your calendars live in your calendar account, not on one device. Add a calendar on your phone at lunch and it is on your laptop by the time you sit back down, because Apple, Google, and Outlook carry subscriptions to every device signed into the same account. Get a new phone, sign in, and everything comes back, still updating. There is nothing to export, re-import, or keep in sync by hand.",
            },
            {
                type: "features",
                heading: "Everywhere you are",
                items: [
                    { icon: "devices", title: "Phone, laptop, tablet", body: "Subscribe once and your calendars appear on every device signed in to the same account." },
                    { icon: "sync", title: "Automatic, not manual", body: "Changes flow across your devices on their own, and an offline device catches up the next time it connects." },
                    { icon: "event_available", title: "In your time zone", body: "Start times convert to each device's local time, so the same game reads correctly at home or travelling." },
                    { icon: "cloud_done", title: "Nothing to install", body: "Calendars live in your calendar account, not on any one device." },
                    { icon: "phone_iphone", title: "New phone ready", body: "Sign in and everything comes back, still updating." },
                ],
            },
            {
                type: "callout",
                title: "Keep every device in step",
                body: "Go Stanza+ for automatic sync across all your devices.",
                cta: { label: "See Stanza+", to: "/upgrade/plus" },
            },
        ],
        faqs: [
            { q: "What does cross-device sync do?", a: "It keeps your calendars identical across every device. Add or change a calendar on one device and the update appears on the others automatically." },
            { q: "Which devices does it work on?", a: "Any device signed in to your calendar account, including phone, tablet, and computer, across Apple, Google, and Outlook." },
            { q: "Do I need to install anything on each device?", a: "No. Your calendars live in your calendar account, so they appear on any device signed into it with nothing extra to install." },
            { q: "What happens when I get a new phone?", a: "Sign in and your calendars come back automatically, still updating exactly as before." },
            { q: "What happens if a device is offline?", a: "It catches up the next time it connects and its calendar app refreshes. Nothing is lost." },
        ],
        related: [
            { label: "How calendar apps update", to: "/product/how-calendar-apps-update" },
            { label: "Google Connect", to: "/product/google-connect" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.REAL_TIME_UPDATES,
        blurb: "Changes reach you in near real-time.",
        eyebrow: "Stanza+",
        seoTitle: "Near real-time calendar updates with Stanza+",
        seoDescription: "Free calendars refresh about once a day. Stanza+ pushes schedule changes to your calendar in near real-time, so you are never caught out.",
        hero: {
            title: "Near real-time updates",
            subtitle: "Free calendars refresh about once a day. Stanza+ pushes changes to your calendar as soon as they happen, so a moved game reaches you fast.",
        },
        heroVisual: {
            frames: [
                { badge: "Change detected", title: "Chiefs vs Eagles", rows: [{ label: "Kickoff", meta: "Sun 4:25 PM" }] },
                { badge: "Pushed in seconds", title: "Chiefs vs Eagles", rows: [{ label: "Kickoff", meta: "Sun 8:20 PM" }, { label: "Your calendar", meta: "Updated" }] },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "The nightly refresh, and when it is not enough",
                tldr: "Changes land in minutes",
                body: "Free calendars refresh overnight, which is plenty for a schedule that holds still. Live sports and premieres do not. The NFL flexes a game to primetime, a playoff time firms up the night before, a premiere slides a week, and a calendar that catches up tomorrow morning is a calendar that already let you down. Stanza+ pushes changes out as they happen, so the version on your phone matches the version the league just announced.",
            },
            {
                type: "features",
                heading: "Faster where it counts",
                items: [
                    { icon: "bolt", title: "Pushed, not polled", body: "When a schedule changes, Stanza+ pushes it out quickly instead of waiting for the nightly refresh." },
                    { icon: "notifications_active", title: "Reminders move too", body: "Any reminder you set on an event travels with it when the time changes, so you are alerted at the right moment." },
                    { icon: "verified", title: "Pair with refresh on demand", body: "Before a big game, pull the very latest yourself instead of waiting on your app's next sync." },
                    { icon: "schedule", title: "Nightly on free", body: "Free calendars still refresh overnight; Stanza+ closes the gap to minutes." },
                    { icon: "sports_score", title: "Built for live moments", body: "Flexed kickoffs and TBD playoff times land as they are announced." },
                ],
            },
            {
                type: "callout",
                title: "Never miss a change",
                body: "Go Stanza+ for near real-time schedule updates.",
                cta: { label: "See Stanza+", to: "/upgrade/plus" },
            },
        ],
        faqs: [
            { q: "How fast do Stanza+ calendars update?", a: "Stanza+ pushes changes in near real-time, usually within moments of a schedule change, rather than waiting for the nightly refresh free calendars get." },
            { q: "What counts as an update?", a: "A new event, a moved date or time, a venue change, or a removed event. Any change at the source flows through." },
            { q: "Does my calendar app also need to refresh?", a: "Yes. Stanza keeps the feed current instantly, and your calendar app applies it on its next poll. Refresh on demand lets you pull it immediately." },
            { q: "Will I get notified when something changes?", a: "The change lands on your calendar, and any reminders you set on the event move with it, so you are alerted at the right time." },
            { q: "Does faster updating drain my battery or data?", a: "No. Updates are small text syncs rather than large downloads, so the effect on battery or data is negligible." },
        ],
        related: [
            { label: "Force refresh", to: "/product/force-refresh" },
            { label: "Transparent Updates", to: "/product/transparent-updates" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.EXCLUSIVE_CALENDARS,
        blurb: "Premium calendars for members.",
        eyebrow: "Stanza+",
        seoTitle: "Exclusive calendars for Stanza+ members",
        seoDescription: "Stanza+ members get premium calendars: expanded coverage, early releases, curated collections, and limited-edition drops.",
        hero: {
            title: "Members-only calendars",
            subtitle: "Some of the best calendars are reserved for members: expanded coverage, early releases, curated collections, and limited-edition drops.",
        },
        heroVisual: {
            frames: [
                { badge: "Stanza+ only", title: "Curated collection", rows: [{ label: "Awards season", meta: "Members" }, { label: "Playoff bracket", meta: "Members" }] },
                { badge: "Stanza+ only", title: "Early release", rows: [{ label: "New season drop", meta: "First access" }] },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "Some calendars are members-only",
                tldr: "Members-only coverage",
                body: "Certain calendars on Stanza are reserved for Stanza+ members. Expanded coverage and premium schedules take real work to build and keep accurate. You can find them in the catalog like anything else, and their pages show you what is inside; membership is what lets you add them. Once added, an exclusive calendar behaves like every other Stanza calendar and keeps itself current.",
            },
            {
                type: "features",
                heading: "Reserved for members",
                items: [
                    { icon: "lock", title: "Premium coverage", body: "Expanded sports and entertainment calendars you will not find on the free plan." },
                    { icon: "visibility", title: "Browse before you join", body: "Exclusive calendars are visible in the catalog, so you can see what membership unlocks." },
                    { icon: "sync", title: "Updates like everything else", body: "Once added, an exclusive calendar refreshes on its own like any other subscription." },
                    { icon: "workspace_premium", title: "A membership benefit", body: "Reserved for the members who support the service." },
                    { icon: "devices", title: "Works everywhere", body: "Syncs to Apple, Google, and Outlook like any other calendar." },
                ],
            },
            {
                type: "callout",
                title: "Unlock the full library",
                body: "Go Stanza+ for access to exclusive calendars.",
                cta: { label: "See Stanza+", to: "/upgrade/plus" },
            },
        ],
        faqs: [
            { q: "What are exclusive calendars?", a: "Premium calendars reserved for Stanza+ members, with expanded coverage beyond the free catalog." },
            { q: "Can I see an exclusive calendar before joining?", a: "Yes. They appear in the catalog and their pages show what is inside; joining Stanza+ is what lets you add them to your devices." },
            { q: "Do exclusive calendars update automatically?", a: "Yes. Once added, they stay current on their own like every other Stanza calendar." },
            { q: "Why are some calendars members-only?", a: "They take real work to build and maintain, so they are reserved for the members who support the service." },
        ],
        related: [
            { label: "Unlimited calendars", to: "/product/unlimited-calendars" },
            { label: "Early access", to: "/product/early-access" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.FORCE_REFRESH,
        blurb: "Pull the latest with one tap.",
        eyebrow: "Stanza+",
        seoTitle: "Force refresh: update your calendars on demand with Stanza+",
        seoDescription: "Refresh your Stanza calendars on demand with one tap. Stanza+ lets you pull the latest schedule instantly, great before a big game.",
        hero: {
            title: "Refresh on demand",
            subtitle: "Want to be sure before a big game? Stanza+ lets you refresh your calendars on demand with one tap, so you always have the latest.",
        },
        heroVisual: {
            frames: [
                { badge: "Tap refresh", title: "NFL Schedule", rows: [{ label: "Checking source", meta: "..." }] },
                { badge: "Up to date", title: "NFL Schedule", rows: [{ label: "Chiefs vs Eagles", meta: "Sun 8:20 PM" }, { label: "Refreshed", meta: "Just now" }] },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "For the moments you need to be certain",
                tldr: "Pull the latest with one tap",
                body: "Automatic updates cover the everyday. Refresh on demand is for the moments that matter. Twenty minutes before kickoff, the night of a finale, right after you hear a rumor that the time moved. One tap pulls the latest schedule from the source on the spot, and the last-updated timestamp flips to just now so you can see it worked. No waiting on the next scheduled check.",
            },
            {
                type: "features",
                heading: "Peace of mind on demand",
                items: [
                    { icon: "refresh", title: "One-tap refresh", body: "Pull the latest schedule right now instead of waiting for the next automatic check." },
                    { icon: "sports_score", title: "Great before kickoff", body: "Double check a start time before a big game or premiere so you are never caught out." },
                    { icon: "schedule", title: "See when it last ran", body: "The last-updated timestamp updates the moment you refresh, so you know it worked." },
                    { icon: "devices", title: "Every device follows", body: "The refresh happens at the source; each device picks it up on its next sync." },
                    { icon: "all_inclusive", title: "No limits", body: "Refresh as often as you like, whenever you want to be certain." },
                ],
            },
            {
                type: "callout",
                title: "Always be sure",
                body: "Go Stanza+ to refresh your calendars whenever you want.",
                cta: { label: "See Stanza+", to: "/upgrade/plus" },
            },
        ],
        faqs: [
            { q: "What does force refresh do?", a: "It pulls the latest version of your calendar on demand, so you do not have to wait for the next automatic refresh." },
            { q: "How is this different from automatic updates?", a: "Automatic updates run on a schedule in the background and keep running as usual. Refresh on demand is a manual pull for when you want the very latest right now." },
            { q: "Does force refresh update every device?", a: "It refreshes the calendar at the source. Each device then picks it up on its next sync." },
            { q: "How do I know the refresh worked?", a: "The last-updated timestamp shows the new time immediately after the pull." },
            { q: "How often can I use it?", a: "Whenever you want. Before a kickoff, before a premiere, or any time a schedule feels uncertain." },
        ],
        related: [
            { label: "Near real-time updates", to: "/product/real-time-updates" },
            { label: "Transparent Updates", to: "/product/transparent-updates" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.RECOMMENDATIONS,
        blurb: "Calendars picked for what you follow.",
        eyebrow: "Stanza+",
        seoTitle: "Calendar recommendations picked for you with Stanza+",
        seoDescription: "Stanza+ suggests calendars based on what you already follow, so you discover new teams and shows without searching for them.",
        hero: {
            title: "Calendars picked for you",
            subtitle: "Stanza+ suggests calendars based on what you already follow, so you keep discovering new teams and shows without having to search.",
        },
        heroVisual: {
            frames: [
                { badge: "Because you follow the Lakers", title: "You might like", rows: [{ label: "Warriors", meta: "Add" }, { label: "NBA Playoffs", meta: "Add" }] },
                { badge: "Because you watch The Bear", title: "You might like", rows: [{ label: "The Studio", meta: "Add" }, { label: "FX on Hulu", meta: "Add" }] },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "The calendar you did not know to search for",
                tldr: "Picked from what you follow",
                body: "Recommendations come from what actually goes together. Sibling teams in the league you follow, the show everyone who watches your shows is also watching, the tournament your team just qualified for. You already told us your taste by what you added; suggestions just close the gap between that and what is on Stanza. They are only ever suggestions, and adding one takes a tap.",
            },
            {
                type: "features",
                heading: "Discovery without the search",
                items: [
                    { icon: "trending_up", title: "Based on what you follow", body: "Suggestions come from the calendars you already have, so they actually fit your taste." },
                    { icon: "explore", title: "Find what you would miss", body: "Surface teams, leagues, and shows you did not know were on Stanza." },
                    { icon: "add_circle", title: "One tap to add", body: "A recommendation becomes a live, self-updating calendar in a couple of taps." },
                    { icon: "block", title: "Never automatic", body: "Suggestions only. Nothing is ever added to your calendar for you." },
                    { icon: "tune", title: "Sharpens over time", body: "As your library changes, the suggestions adjust with it." },
                ],
            },
            {
                type: "callout",
                title: "Discover more of what you love",
                body: "Go Stanza+ for recommendations tuned to what you follow.",
                cta: { label: "See Stanza+", to: "/upgrade/plus" },
            },
        ],
        faqs: [
            { q: "How do recommendations work?", a: "Stanza looks at the calendars you already follow and suggests related ones: sibling teams, shows that tend to go together, competitions your teams play in." },
            { q: "Do recommendations change over time?", a: "Yes. As you add and remove calendars, the suggestions adjust to keep pace with what you are into." },
            { q: "Can I ignore a recommendation?", a: "Of course. Recommendations are suggestions, never automatic additions. You choose what gets added." },
            { q: "How do I add a recommended calendar?", a: "Tap it, choose your calendar app, and it starts updating automatically like any other Stanza calendar." },
            { q: "Are recommendations part of Stanza+?", a: "Yes, personalized recommendations are a Stanza+ feature." },
        ],
        related: [
            { label: "Unlimited calendars", to: "/product/unlimited-calendars" },
            { label: "How sports fans use Stanza", to: "/product/how-sports-fans-use-stanza" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.ADD_INDIVIDUAL_EVENTS,
        blurb: "One game, not the season.",
        eyebrow: "Stanza+",
        seoTitle: "Add individual events, not the whole calendar, with Stanza+",
        seoDescription: "Only want one game or one premiere? Stanza+ lets you add a single event instead of the whole calendar, keeping your schedule clean.",
        hero: {
            title: "Just the event you want",
            subtitle: "Only want one game or one premiere? Stanza+ lets you add just that event, so your calendar stays clean and intentional.",
        },
        heroVisual: {
            frames: [
                { badge: "Pick one", title: "Lakers schedule", rows: [{ label: "vs Warriors", meta: "Add event" }, { label: "vs Suns", meta: "Skip" }] },
                { badge: "Added", title: "Your calendar", rows: [{ label: "Lakers vs Warriors", meta: "Wed 7:30 PM" }] },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "One game, not the season",
                tldr: "Add one event, skip the season",
                body: "Sometimes you want the rivalry game, not all 82. The finale, not the whole season. With Stanza+ you open a calendar, pick the one event you care about, and add just that to your phone. The rest of the schedule stays off your calendar, and if the event you added moves, it still updates itself like a full subscription would. Mix single events with full calendars however suits you: your own team in full, one marquee game from everyone else.",
            },
            {
                type: "features",
                heading: "Only what you want",
                items: [
                    { icon: "file_download", title: "Just the event", body: "Add one game or one episode without subscribing to the full calendar." },
                    { icon: "cleaning_services", title: "A cleaner calendar", body: "Keep your schedule focused on the moments you actually care about." },
                    { icon: "bolt", title: "Still kept current", body: "If the event you added moves, it updates on your calendar like everything else." },
                    { icon: "public", title: "In your time zone", body: "Single events convert to your device's local time like everything else." },
                    { icon: "playlist_add", title: "Mix and match", body: "Combine single events with full subscriptions however suits you." },
                ],
            },
            {
                type: "callout",
                title: "Add exactly what you want",
                body: "Go Stanza+ to add individual events, not whole calendars.",
                cta: { label: "See Stanza+", to: "/upgrade/plus" },
            },
        ],
        faqs: [
            { q: "Can I add just one event instead of a whole calendar?", a: "Yes. With Stanza+ you can add a single event, like one game or one premiere, without subscribing to the entire calendar." },
            { q: "Does an individual event still update?", a: "Yes. If it gets rescheduled, it updates on your calendar automatically, just like a full subscription." },
            { q: "How do I add a single event?", a: "Open a calendar, choose the event you want, and add just that one to your calendar app." },
            { q: "Can I add several individual events?", a: "Yes, as many as you like, mixed with full calendars however suits you." },
            { q: "Do individual events show in my time zone?", a: "Yes, converted to your device's local time like everything else on Stanza." },
        ],
        related: [
            { label: "Group calendars", to: "/product/groups" },
            { label: "Unlimited calendars", to: "/product/unlimited-calendars" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.EARLY_ACCESS,
        blurb: "New features, members first.",
        eyebrow: "Stanza+",
        seoTitle: "Early access to new features and drops with Stanza+",
        seoDescription: "Stanza+ members try new calendars, features, and experimental drops before they go live to everyone else.",
        hero: {
            title: "Get early access",
            subtitle: "Be first to try new calendars and features before they roll out to everyone else.",
        },
        heroVisual: {
            frames: [
                { badge: "Members first", title: "New this week", rows: [{ label: "Experimental drop", meta: "Early access" }] },
                { badge: "Coming to everyone later", title: "New this week", rows: [{ label: "Experimental drop", meta: "You are in" }] },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "Members see it first",
                tldr: "New features reach members first",
                body: "When something new rolls out, Stanza+ members get it first. Stanza ships constantly: new coverage, new features, new ways to follow what you love. You see them while they are still being refined, and the feedback members share early is a real part of deciding what ships. No opt-in, no waitlist to watch. New things simply show up for you first.",
            },
            {
                type: "features",
                heading: "First in line",
                items: [
                    { icon: "card_giftcard", title: "New before everyone", body: "Try fresh calendars and features while they are still rolling out." },
                    { icon: "feedback", title: "Shape what ships", body: "Your early use helps decide what becomes part of Stanza for everyone." },
                    { icon: "science", title: "Still being refined", body: "Early features can change before launch, and member feedback is why." },
                    { icon: "workspace_premium", title: "Included with membership", body: "No extra cost and nothing to opt into." },
                    { icon: "update", title: "Always something new", body: "Stanza ships constantly, so early access keeps paying out." },
                ],
            },
            {
                type: "callout",
                title: "Be first",
                body: "Go Stanza+ for early access to new calendars and features.",
                cta: { label: "See Stanza+", to: "/upgrade/plus" },
            },
        ],
        faqs: [
            { q: "What is early access?", a: "Stanza+ members get new features and calendars before they go live to everyone else." },
            { q: "How will I know about early releases?", a: "They surface inside Stanza for members first. There is no announcement to watch for or opt-in to find." },
            { q: "Does early access cost extra?", a: "No, it is included with Stanza+." },
            { q: "Can I give feedback on early features?", a: "Yes, and it matters. What members say while using a feature early helps decide what gets refined and what ships to everyone." },
        ],
        related: [
            { label: "Exclusive calendars", to: "/product/exclusive-calendars" },
            { label: "Suggest a calendar", to: "/product/suggest-calendars" },
        ],
    },
    {
        slug: PRODUCT_PAGE_SLUGS.SUGGEST_CALENDARS,
        blurb: "If we don't have it, we build it.",
        eyebrow: "Stanza+",
        seoTitle: "Suggest new calendars as a Stanza+ member",
        seoDescription: "Cannot find a calendar you want? Stanza+ members can suggest new calendars and help shape what gets added next.",
        hero: {
            title: "Suggest a calendar",
            subtitle: "If we don't have it, we build it. Every request gets a real calendar, kept up to date.",
            request: true,
        },
        heroVisual: {
            frames: [
                { badge: "Your suggestion", title: "Request a calendar", rows: [{ label: "Local derby league", meta: "Submitted" }] },
                { badge: "Added to Stanza", title: "Now available", rows: [{ label: "Local derby league", meta: "Live" }] },
            ],
        },
        blocks: [
            {
                type: "prose",
                heading: "A person builds every one",
                tldr: "Every request gets a real calendar",
                body: "Every calendar request goes to a person who can actually build the calendar. Name a team, a league, a show, or an organizer with a real schedule to follow, and we find the source and put it on Stanza, kept up to date like everything else. Requests from Stanza+ members move to the front of the queue, and once a requested calendar is live, everyone gets to follow it.",
            },
            {
                type: "steps",
                heading: "How a request becomes a calendar",
                items: [
                    { title: "Tell us what is missing", body: "Use the request box on the homepage, or the request option in search when a lookup comes up empty." },
                    { title: "We find the source", body: "A real schedule needs a reliable source. Clear, specific requests are the fastest to build." },
                    { title: "It goes live for everyone", body: "The calendar lands in the catalog, kept current like any other Stanza calendar." },
                ],
            },
            {
                type: "callout",
                title: "Missing a calendar?",
                body: "Go Stanza+ and your requests move to the front of the queue.",
                cta: { label: "See Stanza+", to: "/upgrade/plus" },
            },
        ],
        faqs: [
            { q: "Can I request a calendar that does not exist yet?", a: "Yes. Name a team, league, show, or organizer and we will work on building it. Requests from Stanza+ members are prioritized." },
            { q: "How do I request a calendar?", a: "Use the request box at the bottom of the homepage, or the request option when a search comes up empty." },
            { q: "What makes a good request?", a: "A specific name with a real schedule behind it. The clearer the request, the easier it is to find a source and build the calendar." },
            { q: "How long does it take?", a: "It varies with the source. Some calendars arrive quickly, others take longer to build a reliable feed for." },
            { q: "Who can follow a calendar I requested?", a: "Everyone. Once it is live, the whole community that wanted that schedule gets it." },
        ],
        related: [
            { label: "Early access", to: "/product/early-access" },
            { label: "Recommendations", to: "/product/recommendations" },
        ],
    },
];
//# sourceMappingURL=productPages.js.map